import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Store } from "Application/Store/Store";
import { animationState, togglePlayState, pauseAnimation } from "../utils/animationState";
import { startAnimationTimer } from "../utils/animationTimer";
import {
    calculateTimelineWidths,
    calculateNumberPosition,
    getTreatmentPlanningSteps,
} from "../utils/playerMath";

const SPEED_OPTIONS = [100, 200, 400];

export const usePlayer = (times) => {
    const dispatch = useDispatch();

    const [treatmentPlanningCounter, setTreatmentPlanningCounter] = useState({
        upper: 0,
        lower: 0,
    });
    const [playing, setPlaying] = useState(animationState.play);
    const [speed, setSpeed] = useState(200);
    const [loop, setLoop] = useState(true);
    const [isDragging, setIsDragging] = useState(null);

    const keyFrame = useSelector(state => state.AnimationReducer.keyframe ?? 0);

    const isSceneLoaded = useSelector((state) => state.AnimationReducer.loaded);
    const displayable = useSelector(
        (state) => state.AnimationReducer.animationVisibility
    );

    const steps = times.times;
    const treatmentPlanning = useMemo(() => times.treatmentPlanning || {}, [times.treatmentPlanning]);

    const [maxiStep, mandiStep] = getTreatmentPlanningSteps(treatmentPlanning);
    const { upperWidth, lowerWidth, largestWidth } = calculateTimelineWidths(
        maxiStep,
        mandiStep
    );
    const maxSteps = Math.max(maxiStep, mandiStep);

    const leftValue = calculateNumberPosition(keyFrame, maxSteps, largestWidth);

    const nextFrame = useCallback(
        (k) => {
            const hasNextStep = !!steps[k + 1];
            return hasNextStep ? k + 1 : 0;
        },
        [steps]
    );

    const prevFrame = (k) => {
        const hasPreviousStep = k - 1 >= 0;
        return hasPreviousStep ? k - 1 : steps.length - 1;
    };

    const changeFrame = useCallback(
        (key) => {
            dispatch({ type: "ACTUAL_KEYFRAME", payload: parseInt(key, 10) });
        },
        [dispatch]
    );

    const changeSpeed = () => {
        setSpeed((prev) => {
            const idx = SPEED_OPTIONS.indexOf(prev);
            return SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
        });
    };

    const changeLoop = () => setLoop((prev) => !prev);

    /* Global drag handling */
    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            const isUpperTrack = isDragging === "upper";
            const sliderSelector = isUpperTrack
                ? "#PlayerTimelineContainer"
                : "#PlayerTimelineContainer2";
            const slider = document.querySelector(
                `${sliderSelector} .custom-timeline`
            );
            if (!slider) return;

            const rect = slider.getBoundingClientRect();
            const width = isUpperTrack ? upperWidth : lowerWidth;
            const maxStep = isUpperTrack ? maxiStep : mandiStep;

            const clickX = Math.max(0, Math.min(e.clientX - rect.left, width));
            const percentage = clickX / width;
            const newKey = Math.round(percentage * maxStep);
            const clampedKey = Math.max(0, Math.min(maxStep, newKey));
            changeFrame(clampedKey);
        };

        const handleMouseUp = () => setIsDragging(null);

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, upperWidth, lowerWidth, maxiStep, mandiStep, changeFrame]);

    /* Treatment counters */
    useEffect(() => {
        const hasUpperTreatment = !!treatmentPlanning?.tpUpper;
        if (!hasUpperTreatment) return;

        const upperCount = Object.keys(treatmentPlanning.tpUpper).length - 2;
        const lowerCount = treatmentPlanning.tpLower
            ? Object.keys(treatmentPlanning.tpLower).length - 2
            : 0;

        setTreatmentPlanningCounter((prev) =>
            prev.upper === upperCount && prev.lower === lowerCount
                ? prev
                : { upper: upperCount, lower: lowerCount }
        );
    }, [treatmentPlanning]);

    /* Animation interval — singleton timer, shared across all usePlayer instances */
    useEffect(() => {
        startAnimationTimer({ speed, loop })
    }, [speed, loop])

    /* Sync playing state with global animationState */
    useEffect(() => {
        const id = setInterval(() => {
            if (playing !== animationState.play) {
                setPlaying(animationState.play)
            }
        }, 100)
        return () => clearInterval(id)
    }, [playing])

    /* Static number positions */
    const numberFirstPosition = calculateNumberPosition(0, maxSteps, largestWidth);
    const numberMaxiPosition = calculateNumberPosition(maxiStep, maxSteps, largestWidth);
    const numberMandiPosition = calculateNumberPosition(mandiStep, maxSteps, largestWidth);

    /* Mobile number overlap detection */
    const isAtFrameZero = parseInt(keyFrame, 10) === 0;
    const currentNumber = isAtFrameZero
        ? 0
        : parseInt(keyFrame, 10) + Store.getState().AnimationReducer.increment;
    const digitCount = currentNumber.toString().length;
    const charWidth = 8;
    const padding = 8;
    const pixelThreshold = digitCount * charWidth + padding;

    const cursorPx = (leftValue / 100) * largestWidth;
    const firstPx = (numberFirstPosition / 100) * largestWidth;
    const maxiPx = (numberMaxiPosition / 100) * largestWidth;
    const mandiPx = (numberMandiPosition / 100) * largestWidth;

    const isNearFirst = Math.abs(cursorPx - firstPx) < pixelThreshold;
    const isNearMaxi = Math.abs(cursorPx - maxiPx) < pixelThreshold;
    const isNearMandi = Math.abs(cursorPx - mandiPx) < pixelThreshold;

    const hasAnimation = steps && steps.length > 0;

    const togglePlay = () => {
        togglePlayState();
        setPlaying((p) => !p);
    };

    const handleTimelineMouseDown = (track, e) => {
        const isUpper = track === "upper";
        setIsDragging(track);
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = isUpper ? upperWidth : lowerWidth;
        const maxStep = isUpper ? maxiStep : mandiStep;
        const percentage = clickX / width;
        const newKey = Math.round(percentage * maxStep);
        changeFrame(Math.max(0, Math.min(maxStep, newKey)));
    };

    return {
        playing,
        speed,
        loop,
        leftValue,
        keyFrame,
        isDragging,
        isSceneLoaded,
        displayable,
        treatmentPlanningCounter,
        maxiStep,
        mandiStep,
        upperWidth,
        lowerWidth,
        largestWidth,
        maxSteps,
        numberFirstPosition,
        numberMaxiPosition,
        numberMandiPosition,
        isNearFirst,
        isNearMaxi,
        isNearMandi,
        hasAnimation,
        currentNumber,
        togglePlay,
        changeFrame,
        changeSpeed,
        changeLoop,
        prevFrame,
        nextFrame,
        handleTimelineMouseDown,
        pauseAnimation,
    };
};
